/**
 * Utility functions for managing locale cookies
 */

import { env } from "@/env";

export type Locale = "en" | "ru" | "pt";

export const LOCALE_COOKIE_NAME = "locale";

/**
 * Get locale from cookie
 */
export function getLocaleFromCookie(): Locale {
  if (typeof document === "undefined") {
    return env.VITE_DEFAULT_LOCALE;
  }

  const cookies = document.cookie.split(";");
  const localeCookie = cookies.find((cookie) =>
    cookie.trim().startsWith(`${LOCALE_COOKIE_NAME}=`),
  );

  if (localeCookie) {
    const locale = localeCookie.split("=")[1]?.trim() as Locale;
    if (locale && ["en", "ru", "pt"].includes(locale)) {
      return locale;
    }
  }

  return env.VITE_DEFAULT_LOCALE;
}

/**
 * Set locale cookie
 */
export function setLocaleCookie(locale: Locale): void {
  if (typeof document === "undefined") {
    return;
  }

  // Clear any existing conflicting cookies
  clearConflictingCookies();

  // Set cookie for 1 year
  const expires = new Date();
  expires.setFullYear(expires.getFullYear() + 1);

  document.cookie = `${LOCALE_COOKIE_NAME}=${locale}; expires=${expires.toUTCString()}; path=/; SameSite=Lax`;
}

/**
 * Clear conflicting locale cookies
 */
function clearConflictingCookies(): void {
  if (typeof document === "undefined") {
    return;
  }

  // Clear any existing i18next or Next.js locale cookies
  const cookiesToClear = ["i18next", "NEXT_LOCALE", "locale", "i18nextLng"];

  cookiesToClear.forEach((cookieName) => {
    // Clear for current path
    document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
    // Clear for root path
    document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; SameSite=Lax;`;
  });
}

/**
 * Get locale from URL pathname
 */
export function getLocaleFromPathname(pathname: string): Locale | null {
  if (pathname.startsWith("/ru")) return "ru";
  if (pathname.startsWith("/pt")) return "pt";
  if (pathname.startsWith("/en")) return "en";
  return null; // No locale prefix found
}

/**
 * Get the current locale (from cookie or URL)
 */
export function getCurrentLocale(pathname?: string): Locale {
  // If we have a pathname, check if it has a locale prefix
  if (pathname) {
    const urlLocale = getLocaleFromPathname(pathname);
    if (urlLocale) {
      // URL has locale prefix - use it and set the cookie
      setLocaleCookie(urlLocale);
      return urlLocale;
    }
    // URL doesn't have locale prefix - use existing cookie
    const cookieLocale = getLocaleFromCookie();
    return cookieLocale;
  }

  // No pathname - use cookie
  return getLocaleFromCookie();
}
